import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  AlertTriangle,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Database,
  Download,
  GitBranch,
  Heart,
  History,
  MapPin,
  Pencil,
  Route as RouteIcon,
  Search,
  ShieldQuestion,
  Trash2,
  TriangleAlert
} from 'lucide-react';
import PageHeader from '../../components/ui/PageHeader';
import StatPill from '../../components/ui/StatPill';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Skeleton from '../../components/ui/Skeleton';
import { Card, CardContent } from '../../components/ui/Card';
import { Table, TBody, TH, THead, TR, TD, TableScroller } from '../../components/ui/Table';
import VersionHistoryModal from '../../components/admin/VersionHistoryModal';
import { pathForTab } from '../../utils/routeManagementHelpers';
import {
  getVersioningOverview,
  getDataQuality,
  exportGtfs,
  bulkDeleteStops,
  bulkDeleteBusRoutes
} from '../../utils/api';
import { relativeTime } from '../../utils/commandCenterHelpers';

const TABS = [
  { key: 'entities', label: 'Versioned entities', icon: GitBranch },
  { key: 'quality', label: 'Data quality', icon: ShieldQuestion }
];

const PAGE_SIZE = 25;

export default function DataGovernancePage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('entities');
  const [overview, setOverview] = useState(null);
  const [quality, setQuality] = useState(null);
  const [loading, setLoading] = useState(true);
  const [historyId, setHistoryId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, dq] = await Promise.all([getVersioningOverview(), getDataQuality()]);
      setOverview(ov.data);
      setQuality(dq.data);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load governance data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /*
   * Deep-link into the real map editor to fix an entity, then governance reloads
   * when the user returns.
   *
   * Targets the workspace tab that handles the entity directly. Going via "/"
   * would be redirected to the dashboard, and a redirect does not carry router
   * state — the edit intent would be dropped and nothing would open.
   */
  const fixOnMap = (type, id) =>
    navigate(pathForTab(type === 'route' ? 'routes' : 'stops'), {
      state: { edit: { type, id } }
    });

  async function downloadGtfs() {
    setExporting(true);
    try {
      const res = await exportGtfs();
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'gtfs-export.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 500);
      toast.success('GTFS feed exported');
    } catch (error) {
      toast.error('GTFS export failed');
    } finally {
      setExporting(false);
    }
  }

  const issues = quality ? totalIssues(quality.summary) : 0;

  return (
    <>
      <PageHeader
        eyebrow="Super Admin"
        title="Data governance"
        subtitle="Inspect every versioned record, roll back to any point in time, and keep the dataset clean."
        icon={Database}
        pills={[
          <StatPill key="e" icon={GitBranch} label="Versioned" value={overview?.summary.totalEntities ?? 0} />,
          <StatPill key="v" icon={History} label="Total versions" value={overview?.summary.totalVersions ?? 0} />
        ]}
        actions={
          <Button
            variant="secondary"
            leftIcon={Download}
            loading={exporting}
            onClick={downloadGtfs}
            className="bg-white/10 border-white/15 text-white hover:bg-white/20"
          >
            Export GTFS
          </Button>
        }
      />

      <HealthOverview
        loading={loading}
        overview={overview}
        quality={quality}
        onReviewIssues={() => setTab('quality')}
      />

      {/* tabs */}
      <div className="mt-5 flex items-center gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-1.5 px-4 h-10 text-[13px] font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? 'border-brand-500 text-brand-700'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
            {t.key === 'quality' && issues > 0 && (
              <Badge variant="warning" size="sm">{issues}</Badge>
            )}
          </button>
        ))}
      </div>

      {tab === 'entities' ? (
        <EntitiesTab
          loading={loading}
          entities={overview?.entities || []}
          onView={setHistoryId}
          onFix={fixOnMap}
        />
      ) : (
        <QualityTab loading={loading} quality={quality} onReload={load} onFix={fixOnMap} />
      )}

      <VersionHistoryModal
        open={!!historyId}
        entityId={historyId}
        onClose={() => setHistoryId(null)}
        onChanged={load}
      />
    </>
  );
}

function totalIssues(summary) {
  if (!summary) return 0;
  return (
    (summary.orphanStops || 0) +
    (summary.duplicateCoordinateGroups || 0) +
    (summary.suspiciousCoordinates || 0) +
    (summary.routeIssues || 0)
  );
}

// ---------------------------------------------------------------------------
// Health overview header
// ---------------------------------------------------------------------------

function HealthOverview({ loading, overview, quality, onReviewIssues }) {
  if (loading || !overview || !quality) {
    return <Skeleton className="mt-5 h-28 rounded-2xl" />;
  }

  const totalEntities =
    (quality.summary.totalStops || 0) + (quality.summary.totalRoutes || 0);
  const issues = totalIssues(quality.summary);
  const score = totalEntities
    ? Math.max(0, Math.min(100, Math.round(100 - (issues / totalEntities) * 100)))
    : 100;
  const tone = score >= 90 ? 'ok' : score >= 70 ? 'warn' : 'bad';
  const ring = { ok: 'text-emerald-600', warn: 'text-amber-600', bad: 'text-rose-600' }[tone];
  const dot = { ok: 'bg-emerald-500', warn: 'bg-amber-500', bad: 'bg-rose-500' }[tone];

  return (
    <div className="mt-5 rounded-2xl border border-slate-200 bg-white shadow-soft p-5">
      <div className="flex flex-col lg:flex-row lg:items-center gap-5">
        <div className="flex items-center gap-4 lg:w-64 shrink-0">
          <span className="h-12 w-12 rounded-xl bg-slate-900 text-white inline-flex items-center justify-center">
            <Heart className="h-5 w-5" />
          </span>
          <div>
            <p className="text-[11.5px] uppercase tracking-wide text-slate-400">Data health</p>
            <p className={`text-[30px] font-semibold leading-none tabular-nums ${ring}`}>{score}%</p>
            <p className="mt-1 flex items-center gap-1.5 text-[12px] text-slate-500">
              <span className={`h-2 w-2 rounded-full ${dot}`} />
              {issues === 0 ? 'No open issues' : `${issues} open issue${issues === 1 ? '' : 's'}`}
            </p>
          </div>
        </div>

        <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <HealthTile label="Versioned" value={overview.summary.totalEntities} />
          <HealthTile label="Versions" value={overview.summary.totalVersions} />
          <HealthTile label="With history" value={overview.summary.entitiesWithHistory} />
          <HealthTile label="Open issues" value={issues} tone={issues ? 'warn' : 'ok'} />
        </div>

        {issues > 0 && (
          <Button variant="secondary" rightIcon={ArrowRight} onClick={onReviewIssues} className="shrink-0">
            Review issues
          </Button>
        )}
      </div>
    </div>
  );
}

function HealthTile({ label, value, tone }) {
  const text = tone === 'warn' && value ? 'text-amber-600' : 'text-slate-900';
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
      <p className="text-[10.5px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-0.5 text-[20px] font-semibold tabular-nums ${text}`}>{value ?? 0}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Versioned entities — searchable, filterable, sortable, paginated
// ---------------------------------------------------------------------------

const ENTITY_SORTS = {
  recent: { label: 'Last saved', cmp: (a, b) => new Date(b.lastSavedAt) - new Date(a.lastSavedAt) },
  versions: { label: 'Most versions', cmp: (a, b) => b.totalVersions - a.totalVersions },
  changes: { label: 'Most changes', cmp: (a, b) => b.totalChangeRows - a.totalChangeRows },
  name: { label: 'Name A–Z', cmp: (a, b) => a.name.localeCompare(b.name) }
};

function EntitiesTab({ loading, entities, onView, onFix }) {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [historyFilter, setHistoryFilter] = useState('all');
  const [sort, setSort] = useState('recent');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    let list = [...entities];
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((e) => e.name.toLowerCase().includes(q) || e.entityRef.toLowerCase().includes(q));
    if (typeFilter !== 'all') list = list.filter((e) => e.entityType === typeFilter);
    if (historyFilter === 'history') list = list.filter((e) => e.hasRealHistory);
    list.sort(ENTITY_SORTS[sort].cmp);
    return list;
  }, [entities, search, typeFilter, historyFilter, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search, typeFilter, historyFilter, sort]);

  return (
    <div className="mt-4 rounded-2xl border border-slate-200 bg-white shadow-soft">
      <div className="px-5 py-3.5 border-b border-slate-100 flex flex-col lg:flex-row gap-2.5">
        <Input
          leftIcon={Search}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or id"
          className="lg:flex-1"
        />
        <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="lg:w-36">
          <option value="all">All types</option>
          <option value="route">Routes</option>
          <option value="stop">Stops</option>
        </Select>
        <Select value={historyFilter} onChange={(e) => setHistoryFilter(e.target.value)} className="lg:w-40">
          <option value="all">All records</option>
          <option value="history">Has real history</option>
        </Select>
        <Select value={sort} onChange={(e) => setSort(e.target.value)} className="lg:w-40">
          {Object.entries(ENTITY_SORTS).map(([key, { label }]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </Select>
      </div>

      {loading ? (
        <div className="p-4 space-y-2">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 rounded-lg" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Empty title="No versioned records" hint="Create or edit a stop or route, or adjust the filters above." />
      ) : (
        <>
          <TableScroller>
            <Table>
              <THead>
                <TR hoverable={false}>
                  <TH>Entity</TH>
                  <TH>Type</TH>
                  <TH>Current</TH>
                  <TH>Versions</TH>
                  <TH>Changes</TH>
                  <TH>Last saved</TH>
                  <TH className="text-right">Actions</TH>
                </TR>
              </THead>
              <TBody>
                {pageItems.map((e) => (
                  <TR key={`${e.entityType}-${e.entityRef}`}>
                    <TD>
                      <div className="flex items-center gap-2.5">
                        <span className="h-7 w-7 rounded-lg bg-slate-100 text-slate-600 inline-flex items-center justify-center">
                          {e.entityType === 'route' ? <RouteIcon className="h-3.5 w-3.5" /> : <MapPin className="h-3.5 w-3.5" />}
                        </span>
                        <div className="min-w-0">
                          <p className="text-[13px] font-semibold text-slate-900 truncate">{e.name}</p>
                          <p className="text-[11px] text-slate-400 font-mono truncate">{e.entityRef}</p>
                        </div>
                      </div>
                    </TD>
                    <TD><Badge variant="outline" size="sm">{e.label}</Badge></TD>
                    <TD className="tabular-nums font-semibold text-slate-700">V{e.currentVersionNumber ?? '—'}</TD>
                    <TD className="tabular-nums">
                      {e.totalVersions}
                      {e.hasRealHistory && <Badge variant="brand" size="sm" className="ml-1.5">history</Badge>}
                    </TD>
                    <TD className="tabular-nums text-slate-500">{e.totalChangeRows}</TD>
                    <TD className="text-slate-500">
                      <span className="block text-[12.5px]">{relativeTime(e.lastSavedAt)}</span>
                      <span className="block text-[11px] text-slate-400">by {e.lastSavedBy}</span>
                    </TD>
                    <TD className="text-right">
                      <div className="inline-flex items-center gap-1.5">
                        {e.exists && (
                          <Button size="xs" variant="ghost" leftIcon={Pencil} onClick={() => onFix(e.entityType, e.entityRef)}>
                            Edit
                          </Button>
                        )}
                        <Button size="xs" variant="outline" leftIcon={History} onClick={() => onView(e.entityRef)}>
                          History
                        </Button>
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableScroller>
          <Pager
            page={safePage}
            totalPages={totalPages}
            total={filtered.length}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
          />
        </>
      )}
    </div>
  );
}

function Pager({ page, totalPages, total, onPrev, onNext }) {
  if (total <= PAGE_SIZE) {
    return (
      <div className="px-5 py-2.5 border-t border-slate-100 text-[12px] text-slate-400">
        {total} record{total === 1 ? '' : 's'}
      </div>
    );
  }
  return (
    <div className="px-5 py-2.5 border-t border-slate-100 flex items-center justify-between">
      <span className="text-[12px] text-slate-500 tabular-nums">
        Page {page} of {totalPages} · {total} records
      </span>
      <div className="flex items-center gap-1.5">
        <Button size="xs" variant="outline" leftIcon={ChevronLeft} onClick={onPrev} disabled={page <= 1}>
          Prev
        </Button>
        <Button size="xs" variant="outline" rightIcon={ChevronRight} onClick={onNext} disabled={page >= totalPages}>
          Next
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Data quality — actionable + bulk cleanup
// ---------------------------------------------------------------------------

function QualityTab({ loading, quality, onReload, onFix }) {
  if (loading) {
    return (
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-56 rounded-2xl" />)}
      </div>
    );
  }
  if (!quality) return null;

  const { summary, orphanStops, duplicateCoordinates, suspiciousCoordinates, routeIssues } = quality;

  async function deleteStops(ids, label) {
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} ${label}? They move to Trash and can be restored.`)) return;
    try {
      await bulkDeleteStops(ids);
      toast.success(`${ids.length} stop${ids.length === 1 ? '' : 's'} deleted`);
      onReload();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Delete failed');
    }
  }

  async function deleteRoutes(ids) {
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} route${ids.length === 1 ? '' : 's'}? They move to Trash and can be restored.`)) return;
    try {
      await bulkDeleteBusRoutes(ids);
      toast.success(`${ids.length} route${ids.length === 1 ? '' : 's'} deleted`);
      onReload();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Delete failed');
    }
  }

  return (
    <div className="mt-4 space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <QualityStat label="Orphan stops" value={summary.orphanStops} tone={summary.orphanStops ? 'warning' : 'ok'} />
        <QualityStat label="Duplicate coords" value={summary.duplicateCoordinateGroups} tone={summary.duplicateCoordinateGroups ? 'warning' : 'ok'} />
        <QualityStat label="Suspicious coords" value={summary.suspiciousCoordinates} tone={summary.suspiciousCoordinates ? 'danger' : 'ok'} />
        <QualityStat label="Route issues" value={summary.routeIssues} tone={summary.routeIssues ? 'danger' : 'ok'} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <StopIssueList
          title="Suspicious coordinates"
          hint="Null/zero or outside plausible bounds — fix the pin or remove the stop."
          tone="danger"
          items={suspiciousCoordinates}
          render={(s) => (
            <>
              <span className="font-medium text-slate-800">{s.stopName}</span>
              <span className="text-slate-400 font-mono"> · {s.stopId} · {s.lat}, {s.lng}</span>
            </>
          )}
          onFix={(s) => onFix('stop', s.id)}
          onDelete={(ids) => deleteStops(ids, 'suspicious stop(s)')}
        />

        <StopIssueList
          title="Orphan stops"
          hint="Stops not referenced by any route — keep or remove."
          tone="warning"
          items={orphanStops}
          render={(s) => (
            <>
              <span className="font-medium text-slate-800">{s.stopName}</span>
              <span className="text-slate-400 font-mono"> · {s.stopId}</span>
            </>
          )}
          onFix={(s) => onFix('stop', s.id)}
          onDelete={(ids) => deleteStops(ids, 'orphan stop(s)')}
        />

        <DuplicateList items={duplicateCoordinates} onFix={(id) => onFix('stop', id)} />

        <RouteIssueList items={routeIssues} onFix={(id) => onFix('route', id)} onDelete={deleteRoutes} />
      </div>
    </div>
  );
}

function QualityStat({ label, value, tone }) {
  const ring = { ok: 'border-emerald-200', warning: 'border-amber-200', danger: 'border-rose-200' }[tone];
  const text = { ok: 'text-emerald-600', warning: 'text-amber-600', danger: 'text-rose-600' }[tone];
  return (
    <Card className={`border ${ring}`}>
      <CardContent className="py-4">
        <p className="text-[11.5px] uppercase tracking-wide text-slate-400">{label}</p>
        <p className={`mt-1 text-[26px] font-semibold tabular-nums ${value ? text : 'text-slate-900'}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

// Selectable list of stop issues with per-row Fix / Delete + bulk delete.
function StopIssueList({ title, hint, items, render, tone, onFix, onDelete }) {
  const [selected, setSelected] = useState(() => new Set());
  const Icon = tone === 'danger' ? AlertTriangle : ShieldQuestion;
  const color = tone === 'danger' ? 'text-rose-500' : 'text-amber-500';

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const allSelected = items.length > 0 && items.every((s) => selected.has(s.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map((s) => s.id)));

  return (
    <Card>
      <CardContent>
        <div className="flex items-center gap-2 mb-2">
          <Icon className={`h-4 w-4 ${color}`} />
          <p className="text-[13.5px] font-semibold text-slate-900">{title}</p>
          <Badge variant={items.length ? 'warning' : 'success'} size="sm" className="ml-auto">{items.length}</Badge>
        </div>
        <p className="text-[11.5px] text-slate-400 mb-2">{hint}</p>

        {items.length === 0 ? (
          <p className="text-[12.5px] text-emerald-600">All clear ✓</p>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <label className="inline-flex items-center gap-1.5 text-[12px] text-slate-500 cursor-pointer">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} className="rounded border-slate-300" />
                Select all
              </label>
              {selected.size > 0 && (
                <Button size="xs" variant="danger" leftIcon={Trash2} onClick={() => onDelete([...selected])}>
                  Delete {selected.size}
                </Button>
              )}
            </div>
            <ul className="space-y-1 max-h-72 overflow-auto">
              {items.slice(0, 100).map((s) => (
                <li key={s.id} className="flex items-center gap-2 text-[12.5px] border-b border-slate-50 py-1">
                  <input
                    type="checkbox"
                    checked={selected.has(s.id)}
                    onChange={() => toggle(s.id)}
                    className="rounded border-slate-300 shrink-0"
                  />
                  <span className="min-w-0 flex-1 truncate">{render(s)}</span>
                  <button
                    type="button"
                    onClick={() => onFix(s)}
                    className="text-[11.5px] font-medium text-brand-700 hover:text-brand-800 shrink-0"
                  >
                    Fix on map
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete([s.id])}
                    className="text-[11.5px] font-medium text-rose-600 hover:text-rose-700 shrink-0"
                  >
                    Delete
                  </button>
                </li>
              ))}
              {items.length > 100 && (
                <li className="text-[11.5px] text-slate-400 italic">…and {items.length - 100} more</li>
              )}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function DuplicateList({ items, onFix }) {
  return (
    <Card>
      <CardContent>
        <div className="flex items-center gap-2 mb-2">
          <ShieldQuestion className="h-4 w-4 text-amber-500" />
          <p className="text-[13.5px] font-semibold text-slate-900">Duplicate coordinate groups</p>
          <Badge variant={items.length ? 'warning' : 'success'} size="sm" className="ml-auto">{items.length}</Badge>
        </div>
        <p className="text-[11.5px] text-slate-400 mb-2">Multiple stops at the same point — reposition the duplicates.</p>
        {items.length === 0 ? (
          <p className="text-[12.5px] text-emerald-600">All clear ✓</p>
        ) : (
          <ul className="space-y-2 max-h-72 overflow-auto">
            {items.slice(0, 50).map((g, i) => (
              <li key={g.coord || i} className="border-b border-slate-50 pb-2">
                <p className="text-[11.5px] font-mono text-slate-500">{g.coord}</p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {g.stops.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => onFix(s.id)}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-[12px] text-slate-700 hover:border-brand-300 hover:text-brand-700"
                    >
                      <MapPin className="h-3 w-3" /> {s.stopName}
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function RouteIssueList({ items, onFix, onDelete }) {
  return (
    <Card>
      <CardContent>
        <div className="flex items-center gap-2 mb-2">
          <TriangleAlert className="h-4 w-4 text-rose-500" />
          <p className="text-[13.5px] font-semibold text-slate-900">Route issues</p>
          <Badge variant={items.length ? 'warning' : 'success'} size="sm" className="ml-auto">{items.length}</Badge>
        </div>
        <p className="text-[11.5px] text-slate-400 mb-2">Too few stops or duplicate sequence numbers — fix in the builder.</p>
        {items.length === 0 ? (
          <p className="text-[12.5px] text-emerald-600">All clear ✓</p>
        ) : (
          <ul className="space-y-1 max-h-72 overflow-auto">
            {items.slice(0, 100).map((r) => (
              <li key={r.id} className="flex items-center gap-2 text-[12.5px] border-b border-slate-50 py-1">
                <span className="min-w-0 flex-1 truncate">
                  <span className="font-medium text-slate-800">{r.routeName || `Route ${r.routeNumber}`}</span>
                  <span className="text-rose-500"> · {r.problems.join('; ')}</span>
                </span>
                <button
                  type="button"
                  onClick={() => onFix(r.id)}
                  className="text-[11.5px] font-medium text-brand-700 hover:text-brand-800 shrink-0"
                >
                  Fix on map
                </button>
                <button
                  type="button"
                  onClick={() => onDelete([r.id])}
                  className="text-[11.5px] font-medium text-rose-600 hover:text-rose-700 shrink-0"
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function Empty({ title, hint }) {
  return (
    <div className="py-14 text-center">
      <p className="text-[14px] font-medium text-slate-700">{title}</p>
      <p className="text-[12.5px] text-slate-500 mt-1">{hint}</p>
    </div>
  );
}
